import React, { useState, useEffect, useRef, useCallback } from 'react';
import { busRecordsAPI } from '../services/api';
import EnhancedLocationSelector from './EnhancedLocationSelector';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select } from './ui/select';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { useToast } from './ui/toast';
import { 
  Loader2, 
  Bus, 
  Calendar, 
  Clock, 
  Building2, 
  Route, 
  Car, 
  CreditCard, 
  Hash, 
  MapPin,
  Navigation,
  History,
  FileText,
  PlayCircle,
  CheckCircle2,
  X
} from 'lucide-react';

const DRAFT_KEY = 'bus_record_draft';
const IN_PROGRESS_RECORD_ID_KEY = 'in_progress_record_id';
const AUTO_SAVE_INTERVAL = 3000; // 3秒自动保存

// 获取本地日期（YYYY-MM-DD），避免UTC时区问题
const getLocalDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 获取本地时间（HH:MM），避免UTC时区问题
const getLocalTime = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const BusRecordForm = () => {
  const { success, error: showError, info } = useToast();
  const [formData, setFormData] = useState({
    date: getLocalDate(),
    time: getLocalTime(),
    bus_company: '',
    route: '',
    bus_type: '',
    license_plate: '',
    bus_number: '',
    boarding_location: '',
    alighting_location: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [recentRecords, setRecentRecords] = useState([]);
  const [showQuickFill, setShowQuickFill] = useState(false);
  const [inProgressRecordId, setInProgressRecordId] = useState(null); // 保存"进行中"记录的ID
  const autoSaveTimeoutRef = useRef(null);
  const hasLoadedDraftRef = useRef(false);
  const hasInitializedRef = useRef(false); // 标记是否已经初始化过

  const busCompanies = [
    '九巴',
    '城巴',
    '嶼巴',
    '港鐵巴士',
    '其他'
  ];

  const busTypes = [
    'Enviro500 MMC 12米',
    'Enviro500 MMC 12.8米',
    'B8L 12米',
    'B8L 12.8米',
    'MAN A95 12米',
    'MAN A95 12.8米',
    'B9TL 10.6米',
    'B9TL 12米',
    'B9TL 12.8米',
    '比亞迪B12A 12.13m米',
    '比亞迪B12D 11.97米',
    '其他'
  ];

  // 加载最近的"进行中"记录
  const loadInProgressRecord = useCallback(async () => {
    try {
      const response = await busRecordsAPI.getAll();
      const records = response.data || [];
      
      // 查找"进行中"的记录（下车地点为"進行中 - 待填寫"）
      const inProgressRecords = records.filter(
        record => record.alighting_location === '進行中 - 待填寫'
      );
      
      if (inProgressRecords.length > 0) {
        // 按日期和时间排序，获取最新的记录
        const sortedRecords = inProgressRecords.sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.time}`);
          const dateB = new Date(`${b.date}T${b.time}`);
          return dateB - dateA; // 最新的在前
        });
        
        const latestRecord = sortedRecords[0];
        
        // 保存"进行中"记录的ID
        if (latestRecord.id) {
          setInProgressRecordId(latestRecord.id);
          // 持久化到 localStorage
          localStorage.setItem(IN_PROGRESS_RECORD_ID_KEY, latestRecord.id);
          console.log('加载进行中记录 - 记录ID:', latestRecord.id);
        } else {
          console.warn('进行中记录没有ID:', latestRecord);
        }
        
        // 填充表单（但清空下车地点，让用户填写新的下车地点）
        setFormData({
          date: latestRecord.date || getLocalDate(),
          time: latestRecord.time || getLocalTime(),
          bus_company: latestRecord.bus_company || '',
          route: latestRecord.route || '',
          bus_type: latestRecord.bus_type || '',
          license_plate: latestRecord.license_plate || '',
          bus_number: latestRecord.bus_number || '',
          boarding_location: latestRecord.boarding_location || '',
          alighting_location: '' // 清空下车地点，让用户填写
        });
        
        // 使用 Toast 通知
        success(`已自動載入最近的進行中記錄（${latestRecord.route} - ${latestRecord.bus_company}），請填寫下車地點後提交`);
        
        // 标记已加载，可以开始自动保存
        hasLoadedDraftRef.current = true;
        
        return true; // 表示已加载进行中记录
      }
      
      return false; // 没有进行中的记录
    } catch (err) {
      console.error('載入進行中記錄失敗:', err);
      return false;
    }
  }, [success]);

  // 加载草稿和进行中记录
  useEffect(() => {
    // 防止重复初始化
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    // 从 localStorage 恢复"进行中"记录ID
    const savedInProgressId = localStorage.getItem(IN_PROGRESS_RECORD_ID_KEY);
    if (savedInProgressId) {
      setInProgressRecordId(savedInProgressId);
      console.log('从 localStorage 恢复进行中记录ID:', savedInProgressId);
    }
    
    const loadDraft = () => {
      try {
        const draft = localStorage.getItem(DRAFT_KEY);
        if (draft) {
          const parsedDraft = JSON.parse(draft);
          // 检查草稿是否过期（24小时）
          if (parsedDraft.timestamp && Date.now() - parsedDraft.timestamp < 24 * 60 * 60 * 1000) {
            setFormData(parsedDraft.data);
            // 清除"进行中"记录ID，因为草稿不是已保存的记录
            setInProgressRecordId(null);
            localStorage.removeItem(IN_PROGRESS_RECORD_ID_KEY);
            hasLoadedDraftRef.current = true;
            // 使用 Toast 通知
            info('已自動載入上次的草稿，您可以繼續填寫');
            return true; // 已加载草稿
          } else {
            localStorage.removeItem(DRAFT_KEY);
          }
        }
        return false; // 没有草稿
      } catch (err) {
        console.error('載入草稿失敗:', err);
        return false;
      }
    };

    const initializeForm = async () => {
      // 先尝试加载草稿
      const hasDraft = loadDraft();
      
      // 如果没有草稿，尝试加载最近的"进行中"记录
      if (!hasDraft) {
        await loadInProgressRecord();
        // 无论是否找到进行中记录，都标记为已加载（可以开始自动保存）
        hasLoadedDraftRef.current = true;
      }
      // 如果已加载草稿，hasLoadedDraftRef.current 已在 loadDraft 中设置
      
      // 加载最近记录用于快速填充
      loadRecentRecords();
    };

    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时执行一次

  // 加载最近记录用于快速填充
  const loadRecentRecords = async () => {
    try {
      const response = await busRecordsAPI.getAll();
      const records = response.data || [];
      // 获取最近10条记录
      setRecentRecords(records.slice(0, 10));
    } catch (err) {
      console.error('載入最近記錄失敗:', err);
    }
  };

  // 自动保存草稿
  useEffect(() => {
    // 等待初始加载完成后再开始自动保存
    if (!hasLoadedDraftRef.current) return;

    // 清除之前的定时器
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // 延迟保存，避免频繁写入 localStorage
    autoSaveTimeoutRef.current = setTimeout(() => {
      try {
        // 检查表单是否有内容（至少有一个字段被填写）
        const hasContent = 
          formData.bus_company ||
          formData.route ||
          formData.bus_type ||
          formData.boarding_location ||
          formData.alighting_location ||
          formData.license_plate ||
          formData.bus_number;

        if (hasContent) {
          const draft = {
            data: formData,
            timestamp: Date.now()
          };
          localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
          console.log('草稿已自動保存');
        }
      } catch (err) {
        console.error('自動保存草稿失敗:', err);
      }
    }, AUTO_SAVE_INTERVAL);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocationSelect = (location, type) => {
    setFormData(prev => ({
      ...prev,
      [type]: location
    }));
  };

  // 快速填充 - 使用當前日期和時間，其他字段從歷史記錄填充
  const handleQuickFill = (record) => {
    // 清除"进行中"记录ID，因为这是新的记录
    setInProgressRecordId(null);
    localStorage.removeItem(IN_PROGRESS_RECORD_ID_KEY);
    
    setFormData({
      date: getLocalDate(), // 使用當前日期（本地时区）
      time: getLocalTime(), // 使用當前時間（本地时区）
      bus_company: record.bus_company || '',
      route: record.route || '',
      bus_type: record.bus_type || '',
      license_plate: record.license_plate || '',
      bus_number: record.bus_number || '',
      boarding_location: record.boarding_location || '',
      alighting_location: record.alighting_location || ''
    });
    setShowQuickFill(false);
    success('已從歷史記錄填充表單（日期和時間已更新為當前值）');
  };

  // 一键清除表单
  const handleClearForm = () => {
    // 重置表单为初始状态
    setFormData({
      date: getLocalDate(),
      time: getLocalTime(),
      bus_company: '',
      route: '',
      bus_type: '',
      license_plate: '',
      bus_number: '',
      boarding_location: '',
      alighting_location: ''
    });
    
    // 清除草稿
    localStorage.removeItem(DRAFT_KEY);
    
    // 清除"进行中"记录ID
    setInProgressRecordId(null);
    localStorage.removeItem(IN_PROGRESS_RECORD_ID_KEY);
    
    // 清除消息
    setMessage({ type: '', text: '' });
    
    success('表單已清除');
  };

  // 提交为"进行中"记录（下车地点可暂空）
  const handleSubmitInProgress = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // 验证必填字段（上车地点）
    if (!formData.boarding_location || !formData.boarding_location.trim()) {
      showError('請填寫上車地點');
      setMessage({ 
        type: 'destructive', 
        text: '請填寫上車地點' 
      });
      setLoading(false);
      return;
    }

    // 如果下车地点为空，设置为"进行中"占位符
    const submitData = {
      ...formData,
      alighting_location: formData.alighting_location?.trim() || '進行中 - 待填寫'
    };

    try {
      // 如果已有"进行中"记录ID，则更新；否则创建新记录
      if (inProgressRecordId) {
        await busRecordsAPI.update(inProgressRecordId, submitData);
        success('進行中記錄已更新！');
      } else {
        const response = await busRecordsAPI.create(submitData);
        // 根据后端返回的格式获取记录ID
        // axios 响应结构: response.data 是后端返回的数据
        const recordId = response.data?.id || response.data?._id || response.data?.data?.id || response.data?.data?._id;
        if (recordId) {
          setInProgressRecordId(recordId); // 保存新创建的记录ID
          // 持久化到 localStorage
          localStorage.setItem(IN_PROGRESS_RECORD_ID_KEY, recordId);
          console.log('保存为进行中 - 记录ID:', recordId);
        } else {
          // 如果API没有返回ID，尝试从响应中获取
          console.warn('API未返回记录ID，响应数据:', response.data);
          // 重新加载记录列表，尝试找到刚创建的记录
          setTimeout(async () => {
            try {
              const allRecords = await busRecordsAPI.getAll();
              const inProgressRecords = allRecords.data?.filter(
                r => r.alighting_location === '進行中 - 待填寫'
              ) || [];
              if (inProgressRecords.length > 0) {
                const sorted = inProgressRecords.sort((a, b) => {
                  const dateA = new Date(`${a.date}T${a.time}`);
                  const dateB = new Date(`${b.date}T${b.time}`);
                  return dateB - dateA;
                });
                if (sorted[0]?.id) {
                  setInProgressRecordId(sorted[0].id);
                  // 持久化到 localStorage
                  localStorage.setItem(IN_PROGRESS_RECORD_ID_KEY, sorted[0].id);
                  console.log('从记录列表获取ID:', sorted[0].id);
                }
              }
            } catch (err) {
              console.error('重新加载记录失败:', err);
            }
          }, 500);
        }
        success('記錄已保存為進行中，下車後請記得填寫下車地點！');
      }
      
      // 清除草稿
      localStorage.removeItem(DRAFT_KEY);
      
      // 不清除表单，只清空下车地点，方便下车后填写
      setFormData(prev => ({
        ...prev,
        alighting_location: ''
      }));
      
      // 重新加载最近记录
      loadRecentRecords();
    } catch (error) {
      const errorMessage = error.response?.data?.message || '提交失敗，請重試';
      showError(errorMessage);
      setMessage({ 
        type: 'destructive', 
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  // 提交完整记录（所有字段必填）
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // 验证必填字段
    if (!formData.boarding_location || !formData.boarding_location.trim()) {
      showError('請填寫上車地點');
      setMessage({ 
        type: 'destructive', 
        text: '請填寫上車地點' 
      });
      setLoading(false);
      return;
    }

    if (!formData.alighting_location || !formData.alighting_location.trim()) {
      showError('請填寫下車地點');
      setMessage({ 
        type: 'destructive', 
        text: '請填寫下車地點' 
      });
      setLoading(false);
      return;
    }

    // 如果下车地点是占位符，不允许提交
    if (formData.alighting_location.trim() === '進行中 - 待填寫') {
      showError('請填寫真實的下車地點');
      setMessage({ 
        type: 'destructive', 
        text: '請填寫真實的下車地點' 
      });
      setLoading(false);
      return;
    }

    try {
      // 如果有"进行中"记录的ID，则更新该记录
      // 如果没有ID，但表单数据看起来像是从"进行中"记录加载的，尝试查找对应的记录
      let recordIdToUpdate = inProgressRecordId;
      
      if (!recordIdToUpdate) {
        // 尝试从记录列表中找到匹配的"进行中"记录
        try {
          const allRecords = await busRecordsAPI.getAll();
          const inProgressRecords = allRecords.data?.filter(
            r => r.alighting_location === '進行中 - 待填寫' &&
                 r.boarding_location === formData.boarding_location &&
                 r.route === formData.route &&
                 r.bus_company === formData.bus_company
          ) || [];
          
          if (inProgressRecords.length > 0) {
            // 按日期时间排序，取最新的匹配记录
            const sorted = inProgressRecords.sort((a, b) => {
              const dateA = new Date(`${a.date}T${a.time}`);
              const dateB = new Date(`${b.date}T${b.time}`);
              return dateB - dateA;
            });
            recordIdToUpdate = sorted[0]?.id;
            if (recordIdToUpdate) {
              setInProgressRecordId(recordIdToUpdate);
              localStorage.setItem(IN_PROGRESS_RECORD_ID_KEY, recordIdToUpdate);
              console.log('找到匹配的进行中记录ID:', recordIdToUpdate);
            }
          }
        } catch (err) {
          console.error('查找进行中记录失败:', err);
        }
      }
      
      if (recordIdToUpdate) {
        console.log('提交完整记录 - 更新记录ID:', recordIdToUpdate);
        await busRecordsAPI.update(recordIdToUpdate, formData);
        success('進行中記錄已更新為完整記錄！');
        // 清除"进行中"记录ID
        setInProgressRecordId(null);
        localStorage.removeItem(IN_PROGRESS_RECORD_ID_KEY);
      } else {
        console.log('提交完整记录 - 创建新记录（未找到进行中记录）');
      await busRecordsAPI.create(formData);
        success('巴士記錄已成功提交！');
      }
      
      // 清除草稿
      localStorage.removeItem(DRAFT_KEY);
      
      // Reset form
      setFormData({
        date: getLocalDate(),
        time: getLocalTime(),
        bus_company: '',
        route: '',
        bus_type: '',
        license_plate: '',
        bus_number: '',
        boarding_location: '',
        alighting_location: ''
      });
      
      // 清除"进行中"记录ID
      setInProgressRecordId(null);
      localStorage.removeItem(IN_PROGRESS_RECORD_ID_KEY);
      
      // 重新加载最近记录
      loadRecentRecords();
    } catch (error) {
      const errorMessage = error.response?.data?.message || '提交失敗，請重試';
      showError(errorMessage);
      setMessage({ 
        type: 'destructive', 
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="container mx-auto px-2 sm:px-4 py-2 sm:py-4 md:py-6 max-w-6xl flex items-stretch"
      style={{ minHeight: 'calc(100vh - 80px)', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <Card className="shadow-lg w-full flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Bus className="h-6 w-6" />
                巴士記錄表單
            </CardTitle>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowQuickFill(!showQuickFill)}
                disabled={recentRecords.length === 0}
              >
                <History className="h-4 w-4 mr-2" />
                快速填充
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
              {message.text && (
            <Alert 
              variant={message.type === 'success' ? 'success' : message.type} 
              className="mb-6"
            >
              <AlertDescription>{message.text}</AlertDescription>
                </Alert>
              )}

          {/* 快速填充面板 */}
          {showQuickFill && (
            <Card className="mb-6 border-primary">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  從歷史記錄快速填充
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentRecords.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                    {recentRecords.map((record) => (
                      <Button
                        key={record.id}
                        type="button"
                        variant="outline"
                        className="justify-start h-auto p-3"
                        onClick={() => handleQuickFill(record)}
                      >
                        <div className="text-left">
                          <div className="font-medium">{record.route} - {record.bus_company}</div>
                          <div className="text-xs text-muted-foreground">
                            {record.boarding_location || '未填寫'} → {record.alighting_location || '未填寫'}
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>暫無歷史記錄</p>
                    <p className="text-sm mt-1">開始記錄您的巴士行程後，即可使用快速填充功能</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label htmlFor="date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  日期 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="date"
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                  className="w-full"
                      />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time" className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  時間 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="time"
                        type="time"
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        required
                  className="w-full"
                      />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label htmlFor="bus_company" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  巴士公司 <span className="text-destructive">*</span>
                </Label>
                <Select
                  id="bus_company"
                        name="bus_company"
                        value={formData.bus_company}
                        onChange={handleChange}
                        required
                      >
                        <option value="">請選擇巴士公司</option>
                        {busCompanies.map(company => (
                          <option key={company} value={company}>{company}</option>
                        ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="route" className="flex items-center gap-2">
                  <Route className="h-4 w-4 text-muted-foreground" />
                  路線 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="route"
                        type="text"
                        name="route"
                        value={formData.route}
                        onChange={handleChange}
                        placeholder="例如: 1, 2A, 101"
                        required
                  className="w-full"
                      />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label htmlFor="bus_type" className="flex items-center gap-2">
                  <Car className="h-4 w-4 text-muted-foreground" />
                  車型 <span className="text-destructive">*</span>
                </Label>
                <Select
                  id="bus_type"
                        name="bus_type"
                        value={formData.bus_type}
                        onChange={handleChange}
                        required
                      >
                        <option value="">請選擇車型</option>
                        {busTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="license_plate" className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  車牌
                </Label>
                <Input
                  id="license_plate"
                        type="text"
                        name="license_plate"
                        value={formData.license_plate}
                        onChange={handleChange}
                        placeholder="例如: AB1234"
                  className="w-full"
                      />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bus_number" className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                巴士編號
              </Label>
              <Input
                id="bus_number"
                    type="text"
                    name="bus_number"
                    value={formData.bus_number}
                    onChange={handleChange}
                    placeholder="例如: 12345"
                className="w-full"
                  />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Navigation className="h-4 w-4 text-muted-foreground" />
                上車地點 <span className="text-destructive">*</span>
              </Label>
                  <EnhancedLocationSelector
                    value={formData.boarding_location}
                    onSelect={(location) => handleLocationSelect(location, 'boarding_location')}
                    placeholder="請選擇或輸入上車地點"
                    showMap={true}
                    showSearch={true}
                  />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                下車地點 <span className="text-destructive">*</span>
                <span className="text-xs text-muted-foreground ml-2">(可稍後填寫)</span>
              </Label>
                  <EnhancedLocationSelector
                    value={formData.alighting_location}
                    onSelect={(location) => handleLocationSelect(location, 'alighting_location')}
                    placeholder="請選擇或輸入下車地點（可稍後填寫）"
                    showMap={true}
                    showSearch={true}
                  />
            </div>

            <div className="pt-4 sm:pt-6 space-y-3" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  <FileText className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1" />
                  表單會自動保存草稿
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClearForm}
                  disabled={loading}
                  className="text-muted-foreground hover:text-destructive text-xs sm:text-sm"
                >
                  <X className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  清除表單
                </Button>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 justify-end flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={loading || !formData.boarding_location?.trim()}
                  onClick={handleSubmitInProgress}
                  className="flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      保存中...
                    </>
                  ) : (
                    <>
                      <PlayCircle className="h-4 w-4" />
                      保存為進行中
                    </>
                  )}
                </Button>
                  <Button
                    type="submit"
                    size="lg"
                  disabled={loading || !formData.boarding_location?.trim() || !formData.alighting_location?.trim()}
                  className="flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                        提交中...
                      </>
                    ) : (
                      <>
                      <CheckCircle2 className="h-4 w-4" />
                      提交完整記錄
                      </>
                    )}
                  </Button>
                </div>
              <p className="text-xs text-muted-foreground text-right">
                💡 上車時可先保存為「進行中」，下車後再編輯填寫下車地點
              </p>
            </div>
          </form>
        </CardContent>
          </Card>
    </div>
  );
};

export default BusRecordForm;
